
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { FolderIcon, Search, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const ProjectManagement = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['admin-all-projects'],
    queryFn: async () => {
      try {
        // Use a simpler query that avoids complex joins for now
        const { data, error } = await supabase
          .from('projects')
          .select('*, user_id')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        // For each project, fetch the user profile separately
        const projectsWithProfiles = await Promise.all(
          (data || []).map(async (project) => {
            const { data: profileData, error: profileError } = await supabase
              .from('user_profiles')
              .select('first_name, last_name, email')
              .eq('id', project.user_id)
              .single();
            
            if (profileError) {
              console.error('Error fetching profile for user:', project.user_id, profileError);
              return {
                ...project,
                user_profiles: { 
                  first_name: 'Unknown', 
                  last_name: 'User', 
                  email: 'unknown@example.com' 
                }
              };
            }
            
            return {
              ...project,
              user_profiles: profileData
            };
          })
        );
        
        // Also fetch project collaborators
        const projectsWithCollaborators = await Promise.all(
          projectsWithProfiles.map(async (project) => {
            const { data: collaboratorsData, error: collaboratorsError } = await supabase
              .from('project_collaborators')
              .select('user_id, role')
              .eq('project_id', project.id);
            
            if (collaboratorsError) {
              console.error('Error fetching collaborators:', collaboratorsError);
              return {
                ...project,
                project_collaborators: []
              };
            }
            
            return {
              ...project,
              project_collaborators: collaboratorsData
            };
          })
        );
        
        return projectsWithCollaborators;
      } catch (error) {
        console.error('Error loading projects:', error);
        toast.error('Error loading projects');
        return [];
      }
    }
  });

  const filteredProjects = projects?.filter(project => {
    const matchesSearch = 
      project.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.user_profiles?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.user_profiles?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.user_profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold section-header">Project Management</h1>
        <p className="text-muted-foreground section-subheader">View and manage all student projects</p>
      </div>
      
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search projects by title, description, or student name..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-red-500">
              <p>Error loading projects. Please try again later.</p>
            </div>
          </CardContent>
        </Card>
      ) : filteredProjects && filteredProjects.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-6">
            <div className="text-center">
              <FolderIcon className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="mt-2 text-lg font-medium">No projects found</h3>
              <p className="text-muted-foreground mt-1">
                {searchTerm ? "Try adjusting your search terms." : "There are no projects in the system yet."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const ProjectCard = ({ project }) => {
  const ownerName = project.user_profiles ? 
    `${project.user_profiles.first_name || ''} ${project.user_profiles.last_name || ''}`.trim() : 
    'Unknown';
  
  const collaboratorsCount = project.project_collaborators?.length || 0;

  return (
    <Card className="h-full transition-all hover:shadow-md">
      <CardHeader>
        <CardTitle className="flex justify-between items-start">
          <span className="truncate">{project.title}</span>
          {project.score !== null && (
            <Badge variant="outline" className="ml-2 whitespace-nowrap">
              Score: {project.score}/100
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Created on {new Date(project.created_at).toLocaleDateString()}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="line-clamp-2 text-sm text-muted-foreground mb-4">
          {project.description || 'No description provided.'}
        </p>
        <div className="flex items-center text-sm text-muted-foreground">
          <User className="h-4 w-4 mr-1" />
          <span>Owner: {ownerName}</span>
        </div>
        {collaboratorsCount > 0 && (
          <div className="text-sm text-muted-foreground mt-1">
            <span>+ {collaboratorsCount} collaborator{collaboratorsCount !== 1 ? 's' : ''}</span>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between items-center">
        <div>
          {project.submission_url ? (
            <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 font-medium">
              Submitted
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 font-medium">
              Draft
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/admin/projects/${project.id}`}>View Details</Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ProjectManagement;
